import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";

export default class APIPartners extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Get paginated partners
  get = async (
    params: {
      page?: number;
      size?: number;
      load_all?: boolean;
      partner_type?: number;
      name?: string;
      sort_by?: string;
      sort_order?: string;
      modified_since?: string; // ISO timestamp for delta/incremental fetching
      include_contacts?: boolean; // Include partner contacts in response
      include_addresses?: boolean; // Include partner addresses in response
    } = {},
  ): Promise<types.PaginatedPartnersResponse> => {
    let res = await this._fetch({
      url: `/partners/`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create partner
  post = async ({ ...body }: types.PartnerCreate): Promise<types.Partner> => {
    let res = await this._fetch({
      url: `/partners/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get partner by ID
  getPartnerId = async (
    partnerId: UUID,
    params: {
      include_contacts?: boolean;
      include_addresses?: boolean;
    } = {},
  ): Promise<types.Partner> => {
    let res = await this._fetch({
      url: `/partners/${partnerId}`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update partner
  putPartnerId = async (
    partnerId: UUID,
    { ...body }: types.PartnerUpdate,
  ): Promise<types.Partner> => {
    let res = await this._fetch({
      url: `/partners/${partnerId}`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Delete partner
  deletePartnerId = async (partnerId: UUID): Promise<types.Partner> => {
    let res = await this._fetch({
      url: `/partners/${partnerId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Partner contacts and addresses are fully offline-first via the V2 API
  // and `@stockline/web`. The legacy REST endpoints
  // (`/partners/{id}/{contacts,addresses}`) and their corresponding methods
  // were removed; reads now come from Dexie live queries and writes from
  // `usePartner{Address,Contact}Mutations` → `POST /api/v2/...`.

  // Import partners from file
  postFileImport = async (params: types.PartnerFileImport): Promise<any> => {
    let res = await this._fetch({
      url: `/partners/import`,
      method: "POST",
      body: params,
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // CRM Analytics

  // Get partner statistics
  getPartnerIdStatistics = async (
    partnerId: UUID,
  ): Promise<types.PartnerStatistics> => {
    let res = await this._fetch({
      url: `/partners/${partnerId}/statistics`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get partner product statistics with pagination
  getPartnerIdProducts = async (
    partnerId: UUID,
    params: {
      page?: number;
      size?: number;
      sort_by?: string;
      sort_order?: string;
    } = {},
  ): Promise<types.PaginatedProductStatistics> => {
    let res = await this._fetch({
      url: `/partners/${partnerId}/products`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get partner pre-orders with date filtering
  getPartnerIdPreOrders = async (
    partnerId: UUID,
    params: {
      start_date?: string; // YYYY-MM-DD
      end_date?: string; // YYYY-MM-DD
      date_field?: "delivery_date" | "order_date" | "created_at";
    } = {},
  ): Promise<any[]> => {
    let res = await this._fetch({
      url: `/partners/${partnerId}/pre-orders`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get paginated lean order history for a partner (recap pattern)
  getPartnerOrderHistory = async (
    partnerId: UUID,
    params: {
      page?: number;
      page_size?: number;
      start_date?: string; // YYYY-MM-DD
      end_date?: string; // YYYY-MM-DD
    } = {},
  ): Promise<{
    items: any[];
    total: number;
    page: number;
    page_size: number;
  }> => {
    let res = await this._fetch({
      url: `/partners/${partnerId}/pre-orders/history`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Partner Account (Financial)

  // Get partner account information
  getPartnerIdAccount = async (
    partnerId: UUID,
  ): Promise<types.PartnerAccount | null> => {
    let res = await this._fetch({
      url: `/partners/${partnerId}/account`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get all partner accounts (bulk endpoint)
  getAllAccounts = async (
    params: {
      offset?: number;
      limit?: number;
      has_past_due?: boolean;
      over_limit?: boolean;
      load_all?: boolean;
    } = {},
  ): Promise<types.PartnerAccountSummary[]> => {
    let res = await this._fetch({
      url: `/partners/accounts`,
      method: "GET",
      params,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
